// © Microsoft Corporation. All rights reserved.

import { AccentButton, OutlineButton } from "@microsoft/fast-components-react-msft";
import { avatarFallback, enums, exclamation_round, getExternalURL, hasThemeBase, openEditProfilePage, placeholderImages, addOrUpdateTelemetryObject, dispatchTelemetryEvent, renderHybrid, TelemetryEventMap } from "@msnews/social-utils";
import React from "react";
import { renderToString } from "react-dom/server";
import { String } from "typescript-string-operations";
import { SocialProps } from "../Social.props";
import { onboardingPageTelemetryContracts, OnboardingPageTelemetryObjects } from "./OnboardingPage.telemetry";

interface OnboardingPageState {
    step: number;
    createUserInProgress: boolean;
    createUserErrorMessage?: string;
}
export default class OnboardingPage extends React.Component<SocialProps, OnboardingPageState> {
    containerRef: HTMLDivElement;
    fetchAndVerifyMeTimerId: NodeJS.Timer;
    maxRetryCount: number;
    retryCount: number;
    telemetryObjects:  OnboardingPageTelemetryObjects;

    constructor(props) {
        super(props);
        this.containerRef = null;
        this.maxRetryCount = 3;
        this.retryCount = 0;
        this.state = {step: 0, createUserInProgress: false};
    }

    componentDidMount() {
        if (this.containerRef) {
            dispatchTelemetryEvent(TelemetryEventMap.genericLoaded, this.containerRef);
        }
        this.props.usersConnector.fetchMe(true);
        this.fetchAndVerifyMeTimerId = setInterval(this.fetchAndVerifyMe.bind(this), 10000);
    }

    componentDidUpdate(prevProps: SocialProps) {
        if (!prevProps.me && this.props.me) {
            this.props.markTtvr && this.props.markTtvr();
        }
    }

    fetchAndVerifyMe() {
        if (this.retryCount >= this.maxRetryCount || (this.props.me.nameStatus != enums.userNameStatusType.inReview && this.props.me.avatarStatus != enums.userAvatarStatusType.inReview)) {
            clearInterval(this.fetchAndVerifyMeTimerId);
        } else {
            this.props.usersConnector.fetchMe();
            this.retryCount++;
        }
    }

    editProfileHandler(e, theme, market) {
        e.preventDefault();
        openEditProfilePage(theme, market, "_blank");
        this.props.popupConnector.closePopup();
    }

    async continueOnboardingHandler(e) {
        this.setState({createUserInProgress: true, createUserErrorMessage: undefined});
        try {
            await this.props.usersConnector.createUser();
            this.setState({step: 1, createUserInProgress: false});
        } catch (error) {
            if (error.code == 22) {
                // Just ignore duplicate user error since me is already filled with the existing user in this case
                this.setState({step: 1, createUserInProgress: false});
            } else {
                this.setState({createUserErrorMessage: this.getCreateUserErrorMessage(error)});
            }
        }
    }

    getCreateUserErrorMessage(error) {
        switch (error.code) {
            case 0: return this.props.t.OnboardingErrorUnknownMsg;
            case 1: return this.props.t.OnboardingErrorNotSignInMsg;
            case 2: return this.props.t.OnboardingErrorNotAllowMsg;
            case 3: return this.props.t.OnboardingErrorUnknownMsg;
            case 4: return this.props.t.OnboardingErrorUnknownMsg;
            case 5: return this.props.t.OnboardingErrorUnknownMsg;
            case 6: return this.props.t.OnboardingErrorUnknownMsg;
            case 7: return this.props.t.OnboardingErrorUnknownMsg;
            case 8: return this.props.t.OnboardingErrorUnknownMsg;
            case 9: return this.props.t.OnboardingErrorUnknownMsg;
            case 10: return this.props.t.OnboardingErrorUnknownMsg;
            case 20: return this.props.t.OnboardingErrorNoNameMsg;
            case 21: return this.props.t.OnboardingErrorUnknownMsg;
            case 23: return this.props.t.OnboardingFailChildMsg;
        }
    }

    letsGoHandler(e) {
        this.props.popupConnector.closePopup({refreshPage: true});
    }

    render() {

        const { managedClasses, market, me, t, mainTelemetryObject, contentId, theme, contents } = this.props;
        const cmsId = contentId && contents && contents[contentId] && contents[contentId].cmsId;
        const { step } = this.state;

        if (!me) { return null; }

        const { primaryAvatar, avatarStatus, ageGroup } = me;
        const isChild = ageGroup == enums.userAgeGroup.under18;

        /** Generate Telemetry Objects */
        if (mainTelemetryObject) {
            const onboardingStep2TelemetryObject = addOrUpdateTelemetryObject(mainTelemetryObject, onboardingPageTelemetryContracts.step2ThanksPage, {id: cmsId}, this.telemetryObjects && this.telemetryObjects.step2ThanksPage);
            const containerTelemetryObject = addOrUpdateTelemetryObject(mainTelemetryObject, onboardingPageTelemetryContracts.onboardingPage, {id: cmsId}, this.telemetryObjects && this.telemetryObjects.main);
            this.telemetryObjects = {
                main: containerTelemetryObject,
                step1Continue: addOrUpdateTelemetryObject(containerTelemetryObject, onboardingPageTelemetryContracts.step1Continue, { id: cmsId }, this.telemetryObjects && this.telemetryObjects.step1Continue),
                step1EditProfile: addOrUpdateTelemetryObject(containerTelemetryObject, {...onboardingPageTelemetryContracts.step1EditProfile}, { id: cmsId }, this.telemetryObjects && this.telemetryObjects.step1EditProfile),
                step2ThanksPage: onboardingStep2TelemetryObject,
                step2LetsGo: addOrUpdateTelemetryObject(onboardingStep2TelemetryObject, onboardingPageTelemetryContracts.step2LetsGo, {id: cmsId}, this.telemetryObjects && this.telemetryObjects.step2LetsGo)
            };
        }

        if (isChild) {
            return (<div className={managedClasses.onboardingUnderagePageContainer} ref={(el) => { this.containerRef = el; }}>
                <img
                    alt={t.AriaLabelsProfilePicture}
                    className={`cwt-avatar ${avatarStatus == enums.userAvatarStatusType.notAllow ? "cwt-error":""}`}
                    src={primaryAvatar || placeholderImages.avatar}
                    onError={(e)=>{ avatarFallback(e); }}
                />

                <h2 className="cwt-title"> {t.OnboardingUnderageTitle} </h2>
                <p className="cwt-sub-title"> {t.OnboardingUnderageMsg}  </p>

                <div className="cwt-buttons">
                    <AccentButton className="cwt-fast-button" onClick={this.props.popupConnector.closePopup}> {t.OnboardingCloseWindow} </AccentButton>
                </div>
            </div>);
        }

        return ((step == 0 && this.step0()) || (step == 1 && this.step1()));
    }

    step0() {
        const { t, managedClasses, me, theme, market} = this.props;
        const isSuperApp = hasThemeBase(this.props.theme, enums.theme.superappBase);
        const { primaryAvatar, primaryName, nameStatus, avatarStatus, ageGroup} = me;
        const hasNotAllowInfo = avatarStatus == enums.userAvatarStatusType.notAllow || nameStatus == enums.userNameStatusType.notAllow;
        const hasInReviewInfo = avatarStatus == enums.userAvatarStatusType.inReview || nameStatus == enums.userNameStatusType.inReview;
        const isChild = ageGroup == enums.userAgeGroup.under18;
        const isNoName = me.name == null || me.name.trim() === "";

        const EditButtonWrapperComponent = !hasNotAllowInfo ? OutlineButton : AccentButton;
        return (
            <div className={managedClasses.onboardingPageContainer} {...renderHybrid(this.telemetryObjects && this.telemetryObjects.main, true, (el) => { this.containerRef = el; })}>
                <div className="cwt-ellipse-steps">
                    <span className="cwt-active-step"/><span className="cwt-step"/>
                </div>
                <h2 className="cwt-title"> {t.OnboardingTitle}</h2>
                <p className="cwt-sub-title"> {t.OnboardingSubTitle}  </p>
                <img
                    alt={t.AriaLabelsProfilePicture}
                    className={`cwt-avatar ${avatarStatus == enums.userAvatarStatusType.notAllow ? "cwt-error":""}`}
                    src={primaryAvatar || placeholderImages.avatar}
                    onError={(e)=>{ avatarFallback(e); }}
                />
                <span className={`cwt-name ${nameStatus == enums.userNameStatusType.notAllow ? "cwt-error":""}`}>
                    {primaryName}
                </span>
                <hr className="cwt-h-seperator"/>
                <div className="cwt-status-message">
                    {!hasNotAllowInfo && !hasInReviewInfo && !isChild && !this.state.createUserErrorMessage && <span className="cwt-success-message">{t.OnboardingSuccessMsg}</span>}
                    {avatarStatus == enums.userAvatarStatusType.inReview && <span className="cwt-review-message">{t.OnboardingReviewImageMsg}</span>}
                    {nameStatus == enums.userNameStatusType.inReview && <span className="cwt-review-message">{t.OnboardingReviewNameMsg}</span>}
                    <span className="cwt-error-message">
                        {this.state.createUserErrorMessage &&
                            <i className="cwt-icon">
                                {exclamation_round}
                                <span className="cwt-icon-label">{this.state.createUserErrorMessage}</span>
                            </i>
                        }
                    </span>
                    <span className="cwt-error-message">
                        {avatarStatus == enums.userAvatarStatusType.notAllow &&
                            <i className="cwt-icon">
                                {exclamation_round}
                                <span className="cwt-icon-label">{t.OnboardingFailImageMsg}</span>
                            </i>
                        }
                    </span>
                    <span className="cwt-error-message">
                        {nameStatus == enums.userNameStatusType.notAllow &&
                            <i className="cwt-icon">
                                {exclamation_round}
                                <span className="cwt-icon-label">{t.OnboardingFailNameMsg}</span>
                            </i>
                        }
                    </span>
                    <span className="cwt-error-message">
                        {isChild &&
                            <i className="cwt-icon">
                                {exclamation_round}
                                <span className="cwt-icon-label">{t.OnboardingFailChildMsg}</span>
                            </i>
                        }
                    </span>
                </div>
                <div className="cwt-buttons">
                    {!isSuperApp && !isChild && <a href={getExternalURL(enums.externalUrls.profileSettings, market)} className="cwt-fast-button" onClick={e => this.editProfileHandler(e, theme, market)} {...renderHybrid(this.telemetryObjects && this.telemetryObjects.step1EditProfile, false)}>
                        <EditButtonWrapperComponent>{t.OnboardingEditProfile}</EditButtonWrapperComponent>
                    </a>}
                    {!isChild && !isNoName && <AccentButton disabled={this.state.createUserInProgress} className="cwt-fast-button" onClick={e => this.continueOnboardingHandler(e)} {...renderHybrid(this.telemetryObjects && this.telemetryObjects.step1Continue, false)}> {t.OnboardingContinue} </AccentButton>}
                    {isChild && <AccentButton className="cwt-fast-button" onClick={this.props.popupConnector.closePopup}> {t.OnboardingOK} </AccentButton>}
                </div>
                <span className="cwt-footer-text" dangerouslySetInnerHTML = {{__html: String.Format(t.OnboardingProceedingText, {
                    OnboardingProceedingGuidelines: renderToString(<a href={getExternalURL(isSuperApp ? enums.externalUrls.guidelinePageEmbed : enums.externalUrls.guidelinePage, market)} className="cwt-link" target="_blank">{t.OnboardingProceedingGuidelines}</a>),
                    OnboardingProceedingTermsOfService: renderToString(<a href={`https://www.microsoft.com/${market}/servicesagreement`} className="cwt-link">{t.OnboardingProceedingTermsOfService}</a>),
                    OnboardingProceedingPrivacyAndCookies: renderToString(<a href={`https://privacy.microsoft.com/${market}/privacystatement`} className="cwt-link" target="_blank">{t.OnboardingProceedingPrivacyAndCookies}</a>)
                })
                }}/>
            </div>
        );
    }

    step1() {
        const { t, managedClasses, me} = this.props;
        const { primaryAvatar, name} = me;
        const thanksJoiningTitle = String.Format((t.OnboardingThanksJoiningTitle), {name: name});

        return (
            <div className={managedClasses.onboardingWelcomePageStep2Container} {...renderHybrid(this.telemetryObjects && this.telemetryObjects.step2ThanksPage, true)}>
                <div className="cwt-ellipse-steps">
                    <span className="cwt-step"/><span className="cwt-active-step"/>
                </div>
                <img
                    alt={t.AriaLabelsProfilePicture}
                    className="cwt-avatar-step2"
                    src={primaryAvatar || placeholderImages.avatar}
                />
                <h2 className="cwt-title"> {thanksJoiningTitle}</h2>
                <p className="cwt-sub-title"> {t.OnboardingThanksJoiningSubTitle} </p>

                <div className="cwt-buttons">
                    {<AccentButton className="cwt-fast-button cwt-button-ok" onClick={e => this.letsGoHandler(e)} {...renderHybrid(this.telemetryObjects && this.telemetryObjects.step2LetsGo, false)}> {t.OnboardingOK} </AccentButton>}
                </div>
            </div>
        );
    }
}
